import React, { useCallback } from 'react';
import { Label, Message } from '@naf/input';
import { useEmailValidation } from '@shared/hooks/useEmailValidation';
import { DEFAULT_EMAIL_LABEL, ERROR_MESSAGE_INVALID_EMAIL, getSuggestedEmail } from './EmailFieldContent.utils';
import { FieldWrapper, FullWidthTextField } from './EmailFieldContent.styles';
import { EmailSuggestion } from './EmailSuggestion';

export interface EmailValidationState {
  isValid: boolean | null;
  suggestedHostName: string | null;
  isLoading: boolean;
}

export interface EmailFieldContentProps {
  value: string;
  onChange: (value: string) => void;
  editable: boolean;
  errorMessage?: string;
  emailValidation?: EmailValidationState;
  /** Label shown above the input. Defaults to "E-postadresse". */
  // TODO: Check if we can sync the labels across all email flows (MyProfile, BecomeAMember, IDP, etc.)
  label?: string;
  /** When true, the wrapper adds top margin (used in MyProfile). Defaults to false. */
  hasMargin?: boolean;
  setWidth?: boolean;
}

export const EmailFieldContent = ({
  value,
  onChange,
  editable,
  errorMessage,
  emailValidation,
  label = DEFAULT_EMAIL_LABEL,
  hasMargin = false,
  setWidth,
}: EmailFieldContentProps) => {
  const internalEmailValidation = useEmailValidation(value || '', !emailValidation && editable && !!value);
  const { isValid, suggestedHostName, isLoading } = emailValidation || internalEmailValidation;

  const suggestedEmail = getSuggestedEmail(value, suggestedHostName);
  const showError = isValid === false && !isLoading;
  const showSuggestion = !!suggestedHostName && !!suggestedEmail;
  const displayErrorMessage = showError ? ERROR_MESSAGE_INVALID_EMAIL : errorMessage || '';

  const handleSuggestionClick = useCallback(() => {
    if (suggestedEmail) {
      onChange(suggestedEmail);
    }
  }, [suggestedEmail, onChange]);

  return (
    <FieldWrapper $hasMargin={hasMargin}>
      <Label>{label}</Label>
      <FullWidthTextField
        disabled={!editable}
        error={!!displayErrorMessage}
        message={displayErrorMessage}
        onChange={onChange}
        value={value}
        autoComplete="email"
        type="email"
        $setWidth={setWidth}
      />
      {!!displayErrorMessage && <Message error>{displayErrorMessage}</Message>}
      {showSuggestion && suggestedEmail && (
        <EmailSuggestion suggestedEmail={suggestedEmail} onSuggestionClick={handleSuggestionClick} />
      )}
    </FieldWrapper>
  );
};
